import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Fade,
  Paper,
  IconButton,
  Dialog
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AutoAwesome,
  AutoStories,
  Image as ImageIcon,
  CloudUpload,
  Security,
  Star,
  Close,
  Collections,
  ArrowBackIos,
  ArrowForwardIos
} from '@mui/icons-material';

interface LandingPageProps {
  onGetStarted: () => void;
}

// Gallery images array
const galleryImages = [
  { path: '/examples/alice_caterpillar.png', alt: 'Alice in Wonderland - Caterpillar scene', book: 'Alice in Wonderland' },
  { path: '/examples/alice_cat.png', alt: 'Alice in Wonderland - Cheshire Cat scene', book: 'Alice in Wonderland' },
  { path: '/examples/dracula_count.png', alt: 'Dracula - Count Dracula scene', book: 'Dracula' },
  { path: '/examples/dracula_crawl.png', alt: 'Dracula - Crawling scene', book: 'Dracula' },
  { path: '/examples/sherlock_rider.png', alt: 'Sherlock Holmes - Rider scene', book: 'The Adventures of Sherlock Holmes' },
  { path: '/examples/sherlock_caravan.png', alt: 'Sherlock Holmes - Caravan scene', book: 'The Adventures of Sherlock Holmes' },
  { path: '/examples/grimm_turnip.png', alt: 'Grimm Fairy Tales - Turnip scene', book: 'Grimm Fairy Tales' },
  { path: '/examples/grimm_house.png', alt: 'Grimm Fairy Tales - House scene', book: 'Grimm Fairy Tales' },
  { path: '/examples/80days_elephant2.png', alt: 'Around the World in 80 Days - Elephant scene', book: 'Around the World in 80 Days' },
  { path: '/examples/80days_boat2.png', alt: 'Around the World in 80 Days - Boat scene', book: 'Around the World in 80 Days' },
  { path: '/examples/odyssey_penelope2.png', alt: 'The Odyssey - Penelope scene', book: 'The Odyssey' },
  { path: '/examples/odyssey_telemaches3.png', alt: 'The Odyssey - Telemachus scene', book: 'The Odyssey' },
  { path: '/examples/peterpan_tinkerbell2.png', alt: 'Peter Pan - Tinker Bell scene', book: 'Peter Pan' },
  { path: '/examples/peterpan_neverland.png', alt: 'Peter Pan - Neverland scene', book: 'Peter Pan' },
  { path: '/examples/waroftheworlds_machines2.png', alt: 'War of the Worlds - Machines scene', book: 'War of the Worlds' },
  { path: '/examples/waroftheworlds_artillery2.png', alt: 'War of the Worlds - Artillery scene', book: 'War of the Worlds' }
];

const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted }) => {
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [enlargedImageIndex, setEnlargedImageIndex] = useState<number | null>(null);
  const theme = useTheme();

  // Gallery navigation handlers
  const handleNext = useCallback(() => {
    if (enlargedImageIndex !== null) {
      setEnlargedImageIndex((enlargedImageIndex + 1) % galleryImages.length);
    }
  }, [enlargedImageIndex]);

  const handlePrev = useCallback(() => {
    if (enlargedImageIndex !== null) {
      setEnlargedImageIndex((enlargedImageIndex - 1 + galleryImages.length) % galleryImages.length);
    }
  }, [enlargedImageIndex]);

  const handleCloseGallery = useCallback(() => {
    setEnlargedImageIndex(null);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (enlargedImageIndex === null) return;
      
      if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'Escape') {
        handleCloseGallery();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enlargedImageIndex, handleNext, handlePrev, handleCloseGallery]);

  const features = [
    {
      icon: <CloudUpload sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Simple Book Upload',
      description: 'Just upload the EPUB files you want to read (EPUB is the standard format for e-books). No complex setup or technical knowledge required.',
      color: theme.palette.primary.main
    },
    {
      icon: <AutoAwesome sx={{ fontSize: 40, color: theme.palette.warning.main }} />,
      title: 'Consistent Visual Style',
      description: 'Choose a visual style for the book you\'re reading, consistent colors, mood, and artistic direction that brings the story to life from start to finish.',
      color: theme.palette.warning.main
    },
    {
      icon: <AutoStories sx={{ fontSize: 40, color: theme.palette.success.main }} />,
      title: 'Character Consistency',
      description: 'Characters are remembered and visualized the same way every time they appear. No confusion about who is who—visual continuity throughout your reading experience.',
      color: theme.palette.success.main
    },
    {
      icon: <ImageIcon sx={{ fontSize: 40, color: theme.palette.secondary.main }} />,
      title: 'Images Added to Your Book',
      description: 'AI-generated images are automatically inserted into your e-book. Download your complete illustrated book and read it anywhere you want.',
      color: theme.palette.secondary.main
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Upload a Book',
      description: 'Upload any EPUB file you want to read (the standard e-book format) and our system analyzes the book to understand its structure and content, identifying characters, settings, and key visual moments'
    },
    {
      number: '02',
      title: 'Choose Your Style',
      description: 'Our AI suggests a visual style that fits the book perfectly. This style acts as a baseline for all images—defining colors, mood, and artistic direction—so every illustration feels cohesive. Adjust it until it matches how you want to experience the story: colorful, realistic, moody, dark, or whatever you prefer'
    },
    {
      number: '03',
      title: 'Generate Images',
      description: 'Our system creates beautiful illustrations with consistent character appearances throughout the book. Characters are remembered and visualized similarly every time they appear, maintaining visual continuity as you read'
    },
    {
      number: '04',
      title: 'Download & Enjoy',
      description: 'Download your personalized illustrated edition to read on any e-reader or device you prefer, or enjoy it directly in your browser'
    }
  ];

  // Example book screenshots from Project Gutenberg classics
  const bookExamples = [
    {
      id: 1,
      title: 'Alice\'s Adventures in Wonderland',
      image1: '/examples/alice_caterpillar.png',
      image2: '/examples/alice_cat.png',
      alt1: 'Alice in Wonderland - Caterpillar scene with AI-generated illustration',
      alt2: 'Alice in Wonderland - Cheshire Cat scene with AI-generated illustration'
    },
    {
      id: 2,
      title: 'Dracula',
      image1: '/examples/dracula_count.png',
      image2: '/examples/dracula_crawl.png',
      alt1: 'Dracula - Count Dracula scene with AI-generated illustration',
      alt2: 'Dracula - Crawling scene with AI-generated illustration'
    },
    {
      id: 3,
      title: 'Grimm Fairy Tales',
      image1: '/examples/grimm_turnip.png',
      image2: '/examples/grimm_house.png',
      alt1: 'Grimm Fairy Tales - Turnip scene with AI-generated illustration',
      alt2: 'Grimm Fairy Tales - House scene with AI-generated illustration'
    },
    {
      id: 4,
      title: 'The Adventures of Sherlock Holmes',
      image1: '/examples/sherlock_rider.png',
      image2: '/examples/sherlock_caravan.png',
      alt1: 'Sherlock Holmes - Rider scene with AI-generated illustration',
      alt2: 'Sherlock Holmes - Caravan scene with AI-generated illustration'
    },
    {
      id: 5,
      title: 'Around the World in 80 Days',
      image1: '/examples/80days_elephant2.png',
      image2: '/examples/80days_boat2.png',
      alt1: 'Around the World in 80 Days - Elephant scene with AI-generated illustration',
      alt2: 'Around the World in 80 Days - Boat scene with AI-generated illustration'
    },
    {
      id: 6,
      title: 'The Odyssey',
      image1: '/examples/odyssey_penelope2.png',
      image2: '/examples/odyssey_telemaches3.png',
      alt1: 'The Odyssey - Penelope scene with AI-generated illustration',
      alt2: 'The Odyssey - Telemachus scene with AI-generated illustration'
    },
    {
      id: 7,
      title: 'Peter Pan',
      image1: '/examples/peterpan_tinkerbell2.png',
      image2: '/examples/peterpan_neverland.png',
      alt1: 'Peter Pan - Tinker Bell scene with AI-generated illustration',
      alt2: 'Peter Pan - Neverland scene with AI-generated illustration'
    },
    {
      id: 8,
      title: 'War of the Worlds',
      image1: '/examples/waroftheworlds_machines2.png',
      image2: '/examples/waroftheworlds_artillery2.png',
      alt1: 'War of the Worlds - Machines scene with AI-generated illustration',
      alt2: 'War of the Worlds - Artillery scene with AI-generated illustration'
    }
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: theme.palette.background.default,
      color: theme.palette.text.primary
    }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 16 } }}>
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 4 }}>
              <AutoAwesome sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 800, 
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2.5rem', md: '3.5rem' }
                }}
              >
                TextWiz
              </Typography>
            </Box>
            
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                lineHeight: 1.2,
                color: theme.palette.text.primary
              }}
            >
              See What You Read
            </Typography>
            
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 3, 
                color: theme.palette.text.secondary, 
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.1rem' },
                fontWeight: 500,
                lineHeight: 1.4
              }}
            >
              Created for people with aphantasia, perfect for any book lover
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4, 
                color: theme.palette.text.secondary, 
                maxWidth: 600, 
                mx: 'auto',
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                lineHeight: 1.6
              }}
            >
              TextWiz uses AI to transform your reading experience by adding personalized illustrations directly into the e-books you're reading. See the characters, visualize the settings, and experience key moments, making stories more engaging, memorable, and fun.
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Chip  
                label="10 Free Credits When You Sign Up" 
                sx={{ 
                  backgroundColor: theme.palette.success.main, 
                  color: theme.palette.background.default, 
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 3,
                  px: 2
                }} 
              />
            </Box>
            
            <Button
              variant="contained"
              size="large"
              onClick={onGetStarted}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                px: 4,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: `0 10px 25px ${theme.palette.primary.main}40`,
                minWidth: 200
              }}
            >
              Get Started
            </Button>
          </Box>
        </Fade>

        {/* Aphantasia Info Section */}
        <Fade in timeout={1200}>
          <Paper sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            p: { xs: 4, md: 6 },
            mb: 12,
            textAlign: 'center'
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                color: theme.palette.text.primary
              }}
            >
              What is Aphantasia?
            </Typography>
            
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3, 
                    color: theme.palette.text.primary,
                    lineHeight: 1.6,
                    textAlign: 'left'
                  }}
                >
                  Aphantasia is the inability to create mental images in your mind. When most people read 
                  "imagine a red apple," they can actually see an apple in their mind's eye. 
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3, 
                    color: theme.palette.text.primary,
                    lineHeight: 1.6,
                    textAlign: 'left'
                  }}
                >
                  If you have aphantasia, that "mind's eye" is blank. You understand concepts and can think 
                  about things, but you can't actually visualize them.
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6,
                    textAlign: 'left'
                  }}
                >
                  This affects 2-5% of the population. For some, visual descriptions in books feel 
                  abstract or distant. TextWiz offers an alternative, creating the visual references 
                  that some readers want.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  backgroundColor: theme.palette.secondary.light,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center'
                }}>
                  <Typography variant="h3" sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 1 }}>
                    2-5%
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                    of people have aphantasia
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    That's millions of readers who don't create mental imagery
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* Broader Appeal Section */}
        <Fade in timeout={1400}>
          <Box sx={{ mb: 12, textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 4, 
                fontWeight: 700,
                color: theme.palette.text.primary
              }}
            >
              Perfect for All Readers
            </Typography>
            
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 2 }}>
                    📚 People with Aphantasia
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    See the characters, settings, and worlds described in the books you're reading
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 2 }}>
                    🎨 Visual Learners
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    Enhance comprehension and retention with stunning visual representations
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ color: theme.palette.primary.main, fontWeight: 600, mb: 2 }}>
                    📖 All Book Lovers
                  </Typography>
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    Add a new dimension to your reading experience with beautiful, contextual artwork
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Fade>

        {/* Features Section */}
        <Fade in timeout={1500}>
          <Box sx={{ mb: 12 }}>
            <Typography 
              variant="h3" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: theme.palette.text.primary
              }}
            >
              Built for Visual Accessibility
            </Typography>
            <Typography 
              variant="h6" 
              align="center" 
              sx={{ 
                mb: 8, 
                color: theme.palette.text.secondary,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Born from the aphantasia experience, designed to enhance visual storytelling for all readers
            </Typography>
            
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Fade in timeout={1500 + index * 200}>
                    <Card sx={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 40px ${theme.palette.text.primary}30`,
                        borderColor: feature.color
                      }
                    }}>
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Box sx={{ mb: 3 }}>
                          {feature.icon}
                        </Box>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Fade>

        {/* Download & E-Reader Section */}
        <Fade in timeout={1800}>
          <Paper sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            p: { xs: 4, md: 6 },
            mb: 12,
            textAlign: 'center'
          }}>
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                color: theme.palette.text.primary
              }}
            >
              📱 Read Anywhere, Anytime
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4, 
                color: theme.palette.text.secondary,
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Once your images are generated, they're automatically inserted into the book. Download your personalized illustrated e-book to enjoy on any device or e-reader you prefer.
            </Typography>
            
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2, 
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem',
                      lineHeight: 1.7
                    }}
                  >
                    ✅ <strong>Compatible with your favorite e-readers:</strong> Kindle, Kobo, Apple Books, Google Play Books, and all other devices that support EPUB files
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2, 
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem',
                      lineHeight: 1.7
                    }}
                  >
                    ✅ <strong>Your book, your way:</strong> You pay per image generated, and get a complete downloadable book file with all images embedded - yours to keep forever with no cloud dependency
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2, 
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem',
                      lineHeight: 1.7
                    }}
                  >
                    ✅ <strong>Seamless reading experience:</strong> Images are perfectly integrated into your story flow
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.primary,
                      fontSize: '1.1rem',
                      lineHeight: 1.7
                    }}
                  >
                    ✅ <strong>Offline reading:</strong> Enjoy your illustrated stories without internet connection
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{
                  backgroundColor: theme.palette.primary.light + '20',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center'
                }}>
                  <Typography variant="h5" sx={{ color: theme.palette.primary.main, fontWeight: 700, mb: 1 }}>
                    📖➡️📱
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
                    Your Illustrated Book
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    Download and read on your favorite device or e-reader
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>

        {/* How It Works Section */}
        <Fade in timeout={2000}>
          <Box sx={{ mb: 12 }}>
            <Typography 
              variant="h3" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: theme.palette.text.primary
              }}
            >
              From Text to Vision
            </Typography>
            <Typography 
              variant="h6" 
              align="center" 
              sx={{ 
                mb: 8, 
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Transform your books into visually rich experiences in just four simple steps
            </Typography>
            
            <Grid container spacing={4}>
              {steps.map((step, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Fade in timeout={2000 + index * 300}>
                    <Box sx={{ textAlign: 'center', position: 'relative' }}>
                      <Paper sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        boxShadow: `0 10px 25px ${theme.palette.primary.main}40`
                      }}>
                        <Box
                          sx={{ 
                            color: theme.palette.background.default, 
                            fontWeight: 700,
                            fontSize: '2.125rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            width: '100%'
                          }}
                        >
                          {step.number}
                        </Box>
                      </Paper>
                      
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                        {step.title}
                      </Typography>
                      
                      <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                        {step.description}
                      </Typography>
                      
                      {index < steps.length - 1 && (
                        <Box sx={{
                          position: 'absolute',
                          top: 40,
                          left: '100%',
                          width: '100%',
                          height: 2,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
                          display: { xs: 'none', md: 'block' }
                        }} />
                      )}
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>
            
            {/* View Examples Button */}
            <Box sx={{ textAlign: 'center', mt: 8 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Collections />}
                onClick={() => setExamplesOpen(true)}
                sx={{
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.secondary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light + '20'
                  },
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  borderRadius: 2,
                  minWidth: 200
                }}
              >
                View Examples
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* CTA Section */}
        <Fade in timeout={2500}>
          <Paper sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 4,
            p: { xs: 4, md: 8 },
            textAlign: 'center',
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.secondary.light} 100%)`
          }}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 3, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                color: theme.palette.text.primary
              }}
            >
              Ready to Transform Your Reading?
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 6, 
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Join readers with aphantasia, visual learners, and book enthusiasts who are making their reading experience more visual and engaging
            </Typography>
            
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" sx={{ mb: 4, gap: 2 }}>
              <Chip 
                icon={<Star />} 
                label="10 Free Credits to Start" 
                sx={{ backgroundColor: theme.palette.success.main, color: theme.palette.background.default, fontWeight: 600, fontSize: '0.95rem' }} 
              />
              <Chip 
                icon={<Star />} 
                label="Pay Per Image Generated" 
                sx={{ backgroundColor: theme.palette.success.main, color: theme.palette.background.default, fontWeight: 500 }} 
              />
              <Chip 
                icon={<ImageIcon />} 
                label="Images Auto-Added to Book" 
                sx={{ backgroundColor: theme.palette.secondary.main, color: theme.palette.background.default, fontWeight: 500 }} 
              />
              <Chip 
                icon={<Security />} 
                label="Download & Keep Forever" 
                sx={{ backgroundColor: theme.palette.info.main, color: theme.palette.background.default, fontWeight: 500 }} 
              />
            </Stack>
            
            <Button
              variant="contained"
              size="large"
              onClick={onGetStarted}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                px: 6,
                py: 3,
                fontSize: '1.2rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                boxShadow: `0 15px 35px ${theme.palette.primary.main}40`
              }}
            >
              Start Reading with Pictures
            </Button>
          </Paper>
        </Fade>

        {/* Visible Example Gallery */}
        <Fade in timeout={3000}>
          <Box sx={{ mt: 16, mb: 8 }}>
            <Typography 
              variant="h3" 
              align="center" 
              sx={{ 
                mb: 2, 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: theme.palette.text.primary
              }}
            >
              Example Gallery
            </Typography>
            <Typography 
              variant="h6" 
              align="center" 
              sx={{ 
                mb: 2, 
                color: theme.palette.text.secondary,
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Real examples from classic literature
            </Typography>
            <Typography 
              variant="body2" 
              align="center" 
              sx={{ 
                mb: 6, 
                color: theme.palette.text.disabled,
                fontStyle: 'italic'
              }}
            >
              Click any image to view full size
            </Typography>

            {/* Compact Image Grid - Show first 12 images */}
            <Grid container spacing={3}>
              {galleryImages.slice(0, 12).map((image, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Fade in timeout={3000 + index * 100}>
                    <Paper 
                      sx={{
                        backgroundColor: theme.palette.background.paper,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: `0 12px 30px ${theme.palette.primary.main}40`,
                          borderColor: theme.palette.primary.main
                        }
                      }}
                      onClick={() => setEnlargedImageIndex(index)}
                    >
                      <Box 
                        sx={{
                          height: 300,
                          backgroundImage: `url(${image.path})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      />
                    </Paper>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            {/* View More Button */}
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<Collections />}
                onClick={() => setExamplesOpen(true)}
                sx={{
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    borderColor: theme.palette.primary.dark,
                    backgroundColor: theme.palette.primary.light + '20'
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  textTransform: 'none',
                  borderRadius: 2
                }}
              >
                View Full Gallery
              </Button>
            </Box>
          </Box>
        </Fade>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: { xs: 'center', sm: 'left' } }}>
              © {new Date().getFullYear()} TextWiz. Made for Aphantasia.
            </Typography>
            
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <Box
                component="a"
                href="mailto:support@text-wiz.com"
                sx={{
                  color: theme.palette.text.secondary,
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  '&:hover': {
                    color: theme.palette.primary.light,
                    textDecoration: 'underline',
                  },
                }}
              >
                support@text-wiz.com
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Examples Dialog */}
      <Dialog
        open={examplesOpen}
        onClose={() => setExamplesOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            maxHeight: '95vh',
            cursor: 'default'
          }
        }}
        onClick={(e) => {
          // Close if clicking the backdrop (not the paper content)
          if (e.target === e.currentTarget) {
            setExamplesOpen(false);
          }
        }}
      >
        <Box sx={{ position: 'relative', p: { xs: 3, md: 5 } }}>
          <IconButton
            onClick={() => setExamplesOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: theme.palette.text.secondary,
              zIndex: 1
            }}
          >
            <Close />
          </IconButton>
          
          <Typography variant="h4" sx={{ color: theme.palette.text.primary, mb: 2, fontWeight: 700, textAlign: 'center' }}>
            Example Gallery
          </Typography>
          
          <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 3, textAlign: 'center', maxWidth: 700, mx: 'auto' }}>
            See how TextWiz brings classic literature to life with AI-generated illustrations
          </Typography>
          
          <Typography variant="body2" sx={{ color: theme.palette.text.disabled, mb: 5, textAlign: 'center', fontStyle: 'italic' }}>
            Examples shown using classic literature from Project Gutenberg—a library of license-free e-books 
            including Sherlock Holmes, Alice in Wonderland, Dracula, and thousands more
          </Typography>

          {/* Example Gallery */}
          <Stack spacing={5}>
            {bookExamples.map((example, exampleIndex) => (
              <Box key={example.id}>
                {/* Example Title */}
                <Typography variant="h5" sx={{ 
                  color: theme.palette.primary.main, 
                  fontWeight: 600, 
                  mb: 3, 
                  textAlign: 'center' 
                }}>
                  {example.title}
                </Typography>

                {/* Two Images Side by Side */}
                <Grid container spacing={3}>
                  {/* First Image */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                        borderColor: theme.palette.primary.main
                      }
                    }}
                    onClick={() => {
                      const imageIndex = galleryImages.findIndex(img => img.path === example.image1);
                      if (imageIndex !== -1) setEnlargedImageIndex(imageIndex);
                    }}
                    >
                      <Box 
                        sx={{
                          height: 400,
                          backgroundColor: theme.palette.action.hover,
                          backgroundImage: `url(${example.image1})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}
                      />
                    </Paper>
                  </Grid>

                  {/* Second Image */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 3,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
                        borderColor: theme.palette.primary.main
                      }
                    }}
                    onClick={() => {
                      const imageIndex = galleryImages.findIndex(img => img.path === example.image2);
                      if (imageIndex !== -1) setEnlargedImageIndex(imageIndex);
                    }}
                    >
                      <Box 
                        sx={{
                          height: 400,
                          backgroundColor: theme.palette.action.hover,
                          backgroundImage: `url(${example.image2})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          backgroundRepeat: 'no-repeat'
                        }}
                      />
                    </Paper>
                  </Grid>
                </Grid>

                {/* Divider between examples (except last one) */}
                {exampleIndex < bookExamples.length - 1 && (
                  <Box sx={{ 
                    mt: 5, 
                    height: 1, 
                    backgroundColor: theme.palette.divider 
                  }} />
                )}
              </Box>
            ))}
          </Stack>

          {/* Call to Action */}
          <Box sx={{ textAlign: 'center', mt: 6, pt: 4, borderTop: `2px solid ${theme.palette.divider}` }}>
            <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 2, fontWeight: 600 }}>
              Transform Your Reading Experience
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3, maxWidth: 500, mx: 'auto' }}>
              Upload an e-book you want to read and bring the story to life with AI-generated illustrations
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                setExamplesOpen(false);
                onGetStarted();
              }}
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
                px: 5,
                py: 2.5,
                fontSize: '1.2rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 3,
                boxShadow: `0 8px 24px ${theme.palette.primary.main}40`
              }}
            >
              Get Started Now
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Image Enlargement Modal */}
      <Dialog
        open={enlargedImageIndex !== null}
        onClose={handleCloseGallery}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible'
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center'
          }
        }}
      >
        {enlargedImageIndex !== null && (
          <Box sx={{ position: 'relative', textAlign: 'center' }}>
            {/* Close Button */}
            <IconButton
              onClick={handleCloseGallery}
              sx={{
                position: 'absolute',
                top: -50,
                right: -10,
                color: theme.palette.background.default,
                backgroundColor: theme.palette.text.primary + '80',
                '&:hover': {
                  backgroundColor: theme.palette.text.primary,
                },
                zIndex: 2
              }}
            >
              <Close />
            </IconButton>

            {/* Previous Button */}
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: -60,
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.palette.background.default,
                backgroundColor: theme.palette.text.primary + '80',
                '&:hover': {
                  backgroundColor: theme.palette.text.primary,
                },
                zIndex: 2
              }}
            >
              <ArrowBackIos sx={{ ml: 0.5 }} />
            </IconButton>

            {/* Next Button */}
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: -60,
                top: '50%',
                transform: 'translateY(-50%)',
                color: theme.palette.background.default,
                backgroundColor: theme.palette.text.primary + '80',
                '&:hover': {
                  backgroundColor: theme.palette.text.primary,
                },
                zIndex: 2
              }}
            >
              <ArrowForwardIos />
            </IconButton>
            
            <Box
              component="img"
              src={galleryImages[enlargedImageIndex].path}
              alt={galleryImages[enlargedImageIndex].alt}
              sx={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: 2,
                boxShadow: `0 20px 60px ${theme.palette.text.primary}50`
              }}
            />

            {/* Image Info */}
            <Box 
              sx={{ 
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.7)',
                px: 3,
                py: 1.5,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#fff' }}>
                {galleryImages[enlargedImageIndex].book}
              </Typography>
              <Typography variant="caption" sx={{ color: '#fff', opacity: 0.8 }}>
                {enlargedImageIndex + 1} / {galleryImages.length}
              </Typography>
            </Box>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default LandingPage; 